/**
 * Provider Composition
 *
 * Centralized composition of all application providers.
 * This implements dependency injection - providers live in their domains,
 * but are composed here for a single import point.
 *
 * Architecture:
 * - Infrastructure providers: AppProvider, UIProvider
 * - Domain providers: Imported from features/domains
 * - All providers are composed in the correct order
 * - Single entry point: <ProviderComposition>{children}</ProviderComposition>
 */

"use client";

import React from "react";
import { AppProvider } from "./AppProvider";
import { UIProvider } from "./UIProvider";

// Domain-specific providers (injected from their domains)
// ✅ CRITICAL: Import directly, not from barrel export
import { AuthProvider } from "@/features/identity/providers/AuthProvider";
import { TenantProvider } from "@/features/identity/providers/TenantProvider";

/**
 * ProviderComposition
 *
 * Composes all application providers in the correct order.
 * This is the single entry point for provider setup.
 *
 * Provider order matters:
 * 1. AppProvider (outermost - React Query, Error Boundaries)
 * 2. AuthProvider (authentication state)
 * 3. TenantProvider (tenant/org state - depends on auth)
 * 4. UIProvider (UI state)
 */
export function ProviderComposition({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <AppProvider>
      <AuthProvider>
        <TenantProvider>
          <UIProvider>{children}</UIProvider>
        </TenantProvider>
      </AuthProvider>
    </AppProvider>
  );
}
